// src/components/weeklyReport/StatusOverview.js
//
// The weekly report's one "how it works" card: a coloured tile per status with
// this report's running total and, in a few words, where it's entered.
//   Pre-screens   typed right here (the ones not tied to a study)
//   Screenings / Screen fails / Enrolled/Randomized   a study → Add patient
//   Follow-ups    a study → a number (no patient needed), or a patient
// Tapping a tile other than Pre-screens opens "Pick a study".
// Each tile has its own colour class (weekly-report-tile--*, both modes in
// index.css).

import { REPORT_STATUSES, reportTotals, countValue } from "../../services/weeklyReportModel";
import { INPUT } from "./ui";

const HOW = {
  screening: "Study → Add patient",
  screenFail: "Study → Add patient",
  enrolled: "Study → Add patient · subject # needed",
  followUp: "Study → enter a number · no patient needed",
};
const TITLE = {
  preScreen: "Pre-screens",
  screening: "Screenings",
  screenFail: "Screen fails",
  enrolled: "Enrolled/ Randomized",
  followUp: "Follow-ups",
};

const StatusOverview = ({ studies = [], preScreensTyped = "", onPreScreensChange, onPickStudy }) => {
  const totals = reportTotals(studies);
  const preOnStudies = totals.preScreens;
  const preTotal = preOnStudies + countValue(preScreensTyped);

  return (
    <section
      className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-4 mb-5"
      aria-label="This report's numbers"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 mb-3">
        <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">This report</h2>
        <span className="text-[11px] text-slate-400">Pre-screens: type below. Everything else: pick a study.</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        {REPORT_STATUSES.map((s) => {
          const value = s.id === "preScreen" ? preTotal : totals[s.field];
          const tileClass = `weekly-report-tile weekly-report-tile--${s.id} rounded-xl border p-3 text-left`;
          if (s.id === "preScreen") {
            return (
              <div key={s.id} className={tileClass} title={s.hint}>
                <div className="weekly-report-tile-label text-[10px] uppercase tracking-wider font-semibold">
                  {TITLE[s.id]}
                </div>
                <div className="weekly-report-tile-value text-2xl font-bold tabular-nums leading-tight">{value}</div>
                <label className="mt-1 block">
                  <span className="weekly-report-tile-how block text-[10px]">No study needed:</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    value={preScreensTyped ?? ""}
                    onChange={(e) => onPreScreensChange?.(e.target.value)}
                    className={`${INPUT} mt-0.5 !py-1 !text-sm`}
                    placeholder="0"
                    aria-label="Pre-screens not tied to a study"
                  />
                </label>
                {preOnStudies > 0 && (
                  <div className="weekly-report-tile-how mt-1 text-[10px]">+{preOnStudies} added on studies</div>
                )}
              </div>
            );
          }
          return (
            <button
              key={s.id}
              type="button"
              onClick={onPickStudy}
              className={`${tileClass} hover:brightness-110 transition`}
              title={s.hint}
              aria-label={`${TITLE[s.id]}: ${value}. ${HOW[s.id]}`}
            >
              <div className="weekly-report-tile-label text-[10px] uppercase tracking-wider font-semibold">
                {TITLE[s.id]}
              </div>
              <div className="weekly-report-tile-value text-2xl font-bold tabular-nums leading-tight">{value}</div>
              <div className="weekly-report-tile-how mt-1 text-[10px] leading-snug">{HOW[s.id]}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default StatusOverview;
